import { logger } from "@/lib/logger";

/**
 * Microsoft Graph throttles bursts (a SharePoint "sync all" makes one call per
 * file, one after another) with 429, and sometimes sheds load with 503. Both
 * carry a Retry-After header. Ignoring it means every following call is
 * throttled too, so every Graph call goes through `graphFetch`.
 */

/** Statuses Graph asks us to retry after a wait. */
const RETRYABLE_STATUSES = new Set([429, 503]);

const DEFAULT_MAX_RETRIES = 4;
/** Total time one request may spend waiting before it gives up. */
const DEFAULT_MAX_TOTAL_WAIT_MS = 60_000;
/** Wait for the first retry when Graph sends no Retry-After; doubles after. */
const DEFAULT_RETRY_DELAY_MS = 2_000;

/**
 * How a failed Graph call should be treated:
 * - `notFound`: 404, the item is gone
 * - `forbidden`: 403, or another 4xx; the item can't be reached by this user
 * - `transient`: 429, 5xx, 408, 401 (an expired token) or a network failure;
 *   try again later
 */
export type GraphFailureKind = "notFound" | "forbidden" | "transient";

export class GraphRequestError extends Error {
  /** HTTP status, or undefined when the request never got a response. */
  readonly status: number | undefined;
  readonly kind: GraphFailureKind;
  /** The underlying network error, when there was no response. */
  readonly cause: unknown;

  constructor(message: string, status: number | undefined, cause?: unknown) {
    super(message);
    this.name = "GraphRequestError";
    this.status = status;
    this.kind = classifyStatus(status);
    this.cause = cause;
  }

  /** No Graph token could be acquired; nothing was sent. */
  static tokenUnavailable(): GraphRequestError {
    const error = new GraphRequestError(
      "Could not acquire a Microsoft Graph token",
      undefined
    );
    error.isAuthFailure = true;
    return error;
  }

  /** True when the request failed for want of a token, not on the wire. */
  isAuthFailure = false;
}

const classifyStatus = (status: number | undefined): GraphFailureKind => {
  if (status === undefined) return "transient";
  if (status === 404) return "notFound";
  // A 401 is about our token, never about the item.
  if (status === 401 || status === 408 || status === 429 || status >= 500) {
    return "transient";
  }
  return "forbidden";
};

/**
 * True when the error says nothing about the item itself (throttling, an
 * outage, the network): the caller must report "try again", never "missing".
 */
export const isTransientGraphError = (error: unknown): boolean =>
  !(error instanceof GraphRequestError) || error.kind === "transient";

/**
 * Collects retries that later succeeded, so a whole sync logs one warning
 * instead of one per call. Create one per sync and `flush` it at the end.
 */
export class GraphRetryLog {
  private recoveredRequests = 0;
  private retries = 0;
  private waitedMs = 0;
  private readonly statuses = new Set<number>();
  private readonly operations = new Set<string>();

  record(event: GraphRetryRecovery): void {
    this.recoveredRequests++;
    this.retries += event.retries;
    this.waitedMs += event.waitedMs;
    event.statuses.forEach((status) => this.statuses.add(status));
    this.operations.add(event.operation);
  }

  /** Logs one warning when anything recovered, then resets. */
  flush(context: Record<string, unknown> = {}): void {
    if (this.recoveredRequests > 0) {
      logger.warn("Microsoft Graph throttled requests that later succeeded", {
        operation: "graphFetchRetry",
        recoveredRequests: this.recoveredRequests,
        retries: this.retries,
        waitedMs: this.waitedMs,
        statuses: [...this.statuses],
        graphOperations: [...this.operations],
        ...context,
      });
    }
    this.recoveredRequests = 0;
    this.retries = 0;
    this.waitedMs = 0;
    this.statuses.clear();
    this.operations.clear();
  }
}

export type GraphRetryRecovery = {
  operation: string;
  retries: number;
  waitedMs: number;
  statuses: number[];
};

export type GraphFetchOptions = {
  /** Names the caller in logs. */
  operation: string;
  signal?: AbortSignal;
  /** Collects recovered retries; without it each recovery logs its own warning. */
  retryLog?: GraphRetryLog;
  maxRetries?: number;
  maxTotalWaitMs?: number;
};

/**
 * Parses Retry-After: delta-seconds, or an HTTP date. Returns undefined when
 * the header is absent or unreadable.
 */
export const parseRetryAfterMs = (
  header: string | null,
  now: number = Date.now()
): number | undefined => {
  if (header === null) return undefined;
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed) * 1000;
  const date = Date.parse(trimmed);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, date - now);
};

const abortError = (signal: AbortSignal): unknown =>
  signal.reason ?? new DOMException("The operation was aborted.", "AbortError");

const wait = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(abortError(signal));
      return;
    }
    const onAbort = () => {
      clearTimeout(timer);
      reject(abortError(signal as AbortSignal));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * fetch() for Microsoft Graph. Waits for Retry-After and retries on 429/503,
 * within `maxRetries` and `maxTotalWaitMs`. Resolves only with an OK response;
 * anything else throws a GraphRequestError carrying the HTTP status. An abort
 * rejects with the signal's reason and is never wrapped.
 */
export const graphFetch = async (
  url: string,
  token: string,
  options: GraphFetchOptions,
  init: RequestInit = {}
): Promise<Response> => {
  const {
    operation,
    signal,
    retryLog,
    maxRetries = DEFAULT_MAX_RETRIES,
    maxTotalWaitMs = DEFAULT_MAX_TOTAL_WAIT_MS,
  } = options;

  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);

  let retries = 0;
  let waitedMs = 0;
  const statuses: number[] = [];

  for (;;) {
    let response: Response;
    try {
      response = await fetch(url, { ...init, headers, signal });
    } catch (error) {
      if (signal?.aborted) throw abortError(signal);
      throw new GraphRequestError(
        `Microsoft Graph request failed: ${
          error instanceof Error ? error.message : "network error"
        }`,
        undefined,
        error
      );
    }

    if (response.ok) {
      if (retries > 0) {
        const recovery = { operation, retries, waitedMs, statuses };
        if (retryLog) {
          retryLog.record(recovery);
        } else {
          logger.warn(
            "Microsoft Graph throttled a request that later succeeded",
            {
              ...recovery,
              graphOperation: operation,
              operation: "graphFetchRetry",
            }
          );
        }
      }
      return response;
    }

    const retryAfterMs =
      parseRetryAfterMs(response.headers.get("Retry-After")) ??
      DEFAULT_RETRY_DELAY_MS * 2 ** retries;
    const canRetry =
      RETRYABLE_STATUSES.has(response.status) &&
      retries < maxRetries &&
      waitedMs + retryAfterMs <= maxTotalWaitMs;

    if (!canRetry) {
      throw new GraphRequestError(
        `Microsoft Graph request failed: ${response.status} ${response.statusText}`.trim(),
        response.status
      );
    }

    statuses.push(response.status);
    // Release the throttled response before waiting.
    void response.body?.cancel().catch(() => undefined);
    await wait(retryAfterMs, signal);
    waitedMs += retryAfterMs;
    retries++;
  }
};

/**
 * Danish, user-facing reason for a failed Graph call, for the sync report.
 */
export const describeGraphFailure = (error: unknown): string => {
  if (!(error instanceof GraphRequestError)) return "ukendt fejl";
  if (error.isAuthFailure || error.status === 401) {
    return "kunne ikke logge på Microsoft";
  }
  if (error.status === 429)
    return "SharePoint/OneDrive afviste for mange forespørgsler";
  if (error.status === undefined)
    return "ingen forbindelse til SharePoint/OneDrive";
  if (error.kind === "transient")
    return `SharePoint/OneDrive svarede med fejl ${error.status}`;
  if (error.kind === "notFound") return "findes ikke på SharePoint/OneDrive";
  return `ingen adgang på SharePoint/OneDrive (${error.status})`;
};
